import { get, postJson } from './apiClient';

/**
 * "Check the packing" alerts (D29, MC-5, USC-BUG-0029).
 *
 * When something about a product's packaging looks wrong on a vendor invoice,
 * the server raises an alert on that supplier's item memory. Anyone with Master
 * Catalogue view sees the list; only Master Catalogue edit may mark alerts
 * checked (Preet's Q9). Alerts warn only and never block Confirm & sync (Q11).
 */

export type PackingAlertKind =
  | 'pack_count_missing'
  | 'pack_size_looks_like_one_item'
  | 'pack_unit_unrecognised'
  | 'unit_size_missing'
  | 'memory_disagrees'
  | 'catalogue_contradicts'
  | 'per_unit_jump'
  | 'unchecked_edit'
  /** Informational: packaging was changed on the line, whatever the person's rights (D60, USC-BUG-0055). */
  | 'packaging_edited'
  /** Packing alerts list only: a catalogue row "Needs packaging review" flags (USC-BUG-0060). */
  | 'catalogue_packaging_incomplete';

/** One line's alert as the invoice screen receives it (`GET /vendor-invoices/:id` -> `packingAlerts[lineIndex]`). */
export interface InvoicePackingAlert {
  kind: PackingAlertKind;
  message: string;
  /**
   * A Units per Case the item NAME states ("Milk UHT x 12"), offered as a
   * one-click fill. Evidence for a person, never applied by itself. Only on
   * `pack_size_looks_like_one_item`, and only on the invoice screen.
   */
  suggestedPackCount?: number | null;
}

export interface PackingAlert extends InvoicePackingAlert {
  invoiceId?: string | null;
  invoiceNumber?: string;
  lineIndex?: number | null;
  editedByName?: string;
  detectedAt?: string;
}

export interface PackingAlertProduct {
  _id: string;
  /**
   * `'catalogue'`: a Master Catalogue row whose packaging is incomplete (the
   * "Needs packaging review" filter's rows, USC-BUG-0060). No supplier, nothing
   * to mark checked; it leaves the list when the row's packaging is completed.
   */
  source?: 'catalogue';
  catalogueId?: string;
  vendorId: string | null;
  vendorName?: string | null;
  displayName?: string;
  itemKey: string;
  packingAlerts: PackingAlert[];
  /** What the item memory holds for the packaging fields today. */
  fields?: Partial<Record<'splitUow' | 'splitPackCount' | 'splitUnitSize' | 'splitItemUow', { value: string | number | boolean | null } | null>>;
}

export async function listPackingAlerts(params: { page?: number; limit?: number } = {}): Promise<{
  success: boolean;
  data: PackingAlertProduct[];
  meta: { page: number; limit: number; total: number };
}> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  return get(`/packing-alerts?${qs.toString()}`);
}

/**
 * Mark these products' packaging checked. An unchecked packaging edit is
 * accepted as its invoice line holds it now.
 */
export async function resolvePackingAlerts(
  profileIds: string[]
): Promise<{ success: boolean; data: { resolved: number; accepted: number } }> {
  return postJson('/packing-alerts/resolve', { profileIds });
}
